import React, { useEffect, useState, useRef, useCallback } from "react";
import { Row, Col, Button } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { useNavigate, useLocation } from "react-router-dom";
import { Toast, ToastContainer } from "react-bootstrap";
import { apiCall } from "../../components/service/api-service";
import Loader from "../common/Loader.jsx";

//Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canCreate, canEdit, canDelete, canView } from "../common/permissionUtils";

//API Imports
import { getAccountsListApi, bulkDeleteAccountsApi } from "./accounts-api";
import SearchWithFreeTextAPI from "../common/searchbox-free-text.js";

//SVG Images
import Search from "../../assets/svg/search.svg";
import Filter from "../../assets/svg/filters.svg";
import Plus from "../../assets/svg/add.svg";
import ExcelImportIC from "../../assets/svg/excel-import-icon.svg";
import ScheduleIC from "../../assets/svg/calendar.svg";
import Dropdown from "../../assets/svg/dropdown.svg";
import Dots from "../../assets/svg/groupdots.svg";
import Customize from "../../assets/svg/customize.svg";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";
import Cancel from "../../assets/svg/cancel.svg";
import { ReactComponent as EditRow } from "../../assets/svg/edit.svg";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";
import DeleteModal from "../common/delete-modal.js";
import exportBtn from "../../assets/svg/bl_export_ic.svg";
import uploadIcon from "../../assets/svg/upload-icon.svg";
import CommonFilter from "../common/commonfilter.js";
import ImportFileModal from "../common/importfile-modal.js";
import { sanitizeInput, sanitizeFileName, validateFileSize } from "../common/security-utils.js";
import RecordsPerPage from "../common/RecordsPerPage.jsx";
import AiChat from "../common/ai-chat.js";
import { askAccountChatApi, getAccountAiProvidersApi } from "./accounts-api";

// Persist list UI state (search, pagination, CommonFilter result) across
// navigation to a view page and back. Uses sessionStorage so it survives
// refresh but clears when the browser tab is closed.
const ACCOUNT_LIST_STATE_KEY = "accountListState";

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(ACCOUNT_LIST_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const AccountList = () => {
  const persisted = loadPersistedState();

  //Filter
  const [showFilter, setShowFilter] = useState(false);
  const [showImport, setShowImport] = useState(false);
  // Result of the CommonFilter modal submission (a query-string fragment).
  // Lifted into state (instead of only living inside handleFilter's
  // closure) so it survives pagination, search, and navigating to a view
  // page and back.
  const [activeFilters, setActiveFilters] = useState(persisted.activeFilters ?? "");

  const options = ["Code","Name", "Phone Number", "Account Owner", "Status"];

  //Inputs
  const [status, setStatus] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  //Status Options
  const handleStatusDropdownChange = (option) => {
    setStatus(option.label);
  };

  const handleStatusOnChange = (term) => {
    setStatus(term);
  };

  //Hovered Index
  const [hoveredIndex, setHoveredIndex] = useState(null);

  //Navigate
  const navigate = useNavigate();
  const location = useLocation();

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasCreatePermission = canCreate(rolePermissionList, "Accounts");
  const hasEditPermission = canEdit(rolePermissionList, "Accounts");
  const hasDeletePermission = canDelete(rolePermissionList, "Accounts");
  const hasViewPermission = canView(rolePermissionList, "Accounts");

  //Search
  const [search, setSearch] = useState(persisted.search ?? "");

  //Create Lead Dropdown
  const [open, setOpen] = useState(false);

  //Customize Table Dropdown
  const [showModal, setShowModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(() => Object.fromEntries(options.map((label) => [label, true])));

  const toggleColumn = (label) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };
  const anyColumnSelected = Object.values(selectedColumns).some(Boolean);
  // The header and each data row are separate flex containers (this isn't a
  // real <table>), so flexbox alone can't guarantee their columns line up —
  // each row computes its own flex distribution independently. CSS Grid with
  // an identical grid-template-columns shared by the header and every row
  // forces the same column boundaries everywhere.
  const visibleOptions = options.filter((label) => selectedColumns[label]);
  const columnsGridTemplate = visibleOptions.map(() => "1fr").join(" ") || "1fr";

  // Client-side sort — sorts only the currently loaded page of rows, no API
  // involvement. sortColumn is one of `options`; sortDirection toggles asc/desc;
  // clicking a different column resets to asc.
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSortClick = (label) => {
    if (sortColumn === label) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(label);
      setSortDirection("asc");
    }
  };

  //Delete
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  useEffect(() => {
    if (deleteId != null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  const cancelDelete = () => {
    setIsOpenDelete(false);
    setIsBulkDelete(false);
  };

  const onSuccessDelete = (response) => {
    setDeleteId(null);
    setIsBulkDelete(false);
    setSnackbarMessage(response.message);
    setSnackbarStatus(response.success);
    handleSnackbarShow();
    if (response.success === true) {
      setIsOpenDelete(false);
      setSelectedRows([]);
      setAllSelected(false);
      getAccountsList();
    }
  };

  const handleBulkDelete = () => {
    // 🔒 Permission check for delete
    if (!hasDeletePermission) {
      setSnackbarMessage("You don't have permission to delete accounts");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }
    if (selectedRows.length === 0) return;
    setIsBulkDelete(true);
    setIsOpenDelete(true);
  };

  //Pagination
  const [itemOffset, setItemOffset] = useState(persisted.itemOffset ?? 0);
  const [itemsPerPage, setItemsPerPage] = useState(persisted.itemsPerPage ?? 10);
  const [pageCount, setPageCount] = useState(0);
  const [records, setRecords] = useState(0);

  const handlePageClick = useCallback((event) => {
    const newOffset = (event.selected * itemsPerPage) % accountsList.length;
    setItemOffset(event.selected);
  }, []);

  //Get Accounts List
  const [accountsList, setAccountsList] = useState([]);

  const getAccountsList = async (filters = activeFilters) => {
    setListLoading(true);
    try {
      const response = await getAccountsListApi(`?search=${search}&limit=${itemsPerPage}&skip=${itemOffset * itemsPerPage}${filters ?? ""}`);

      const responseData = response?.data || {};
      const rawAccounts = responseData.accounts || responseData.data || [];

      if (responseData.success === true) {
        setPageCount(responseData.total_pgs || 0);
        setRecords(responseData.total_rec || 0);
        const formatted = formatAccountsData(Array.isArray(rawAccounts) ? rawAccounts : []);
        setAccountsList(formatted);
      }
      return responseData;
    } catch (err) {
      // Error handled silently
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    getAccountsList();
    setSelectedRows([]);
    setAllSelected(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, itemsPerPage, itemOffset, activeFilters]);

  // Sort only the currently loaded page — client-side, no refetch.
  const sortedAccountsList = React.useMemo(() => {
    if (!sortColumn) return accountsList;
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...accountsList].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (sortColumn === "Status") {
        return (aVal === bVal ? 0 : aVal ? 1 : -1) * dir;
      }
      const aStr = (aVal ?? "").toString().toLowerCase();
      const bStr = (bVal ?? "").toString().toLowerCase();
      return aStr.localeCompare(bStr) * dir;
    });
  }, [accountsList, sortColumn, sortDirection]);

  // When the Accounts sidebar menu is clicked it navigates with { resetList: true }.
  // If we're already on this page (no remount), reset live state to defaults so the
  // menu click always gives a fresh list. The flag is cleared so a refresh/back
  // doesn't re-trigger the reset.
  useEffect(() => {
    if (location.state?.resetList) {
      setSearch("");
      setItemOffset(0);
      setItemsPerPage(10);
      setActiveFilters("");
      sessionStorage.removeItem(ACCOUNT_LIST_STATE_KEY);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Persist list UI state so it is restored when returning from a view page.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        ACCOUNT_LIST_STATE_KEY,
        JSON.stringify({
          search,
          itemOffset,
          itemsPerPage,
          activeFilters,
        }),
      );
    } catch {
      // ignore storage write errors (e.g. private mode quota)
    }
  }, [search, itemOffset, itemsPerPage, activeFilters]);

  const formatAccountsData = (rawAccounts) => {
    return rawAccounts.map((account) => ({
      id: account.id,
      "Code": account.code,
      "Name": account.first_name,
      "Phone Number": account.phone_number,
      /* Website: account.website, */
      "Account Owner": account.account_owner,
      Status: Number(account.status) === 1,
    }));
  };

  // state
  const [selectedRows, setSelectedRows] = useState([]);
  const [allSelected, setAllSelected] = useState(false);

  // Sync allSelected state when selectedRows changes
  useEffect(() => {
    if (accountsList.length > 0) {
      setAllSelected(selectedRows.length === accountsList.length);
    }
  }, [selectedRows, accountsList]);

  // handle checkbox logic
  const toggleAll = () => {
    if (allSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(accountsList.map((account) => account.id));
    }
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  //Snackbar
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  const downloadBase64Excel = (base64String, filePrefix, fileSizeErrorMessage) => {
    if (!validateFileSize(base64String, 10)) {
      setSnackbarMessage(fileSizeErrorMessage);
      setSnackbarStatus(false);
      handleSnackbarShow();
      return false;
    }

    const fileName = sanitizeFileName(`${filePrefix}_${Date.now()}.xlsx`);
    const binary = atob(base64String);
    const array = [];

    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }

    const blob = new Blob([new Uint8Array(array)], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  };

  //Template Download
  const downloadTemplate = async () => {
    try {
      const response = await apiCall("GET", "account_template_download");
      if (response?.data?.success === true && response?.data?.base64) {
        downloadBase64Excel(response.data.base64, "Account_Template", "File size exceeds 10MB limit");
      }

      setSnackbarMessage(response?.data?.message || "Unable to download template");
      setSnackbarStatus(response?.data?.success === true);
      handleSnackbarShow();
    } catch (err) {
      // Error handled silently
    } finally {
    }
  };

  const handleFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Security: Validate file type
      const allowedTypes = ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"];
      if (!allowedTypes.includes(file.type)) {
        setSnackbarMessage("Invalid file type. Only Excel files allowed.");
        setSnackbarStatus(false);
        handleSnackbarShow();
        return;
      }

      // Security: Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setSnackbarMessage("File size exceeds 10MB limit");
        setSnackbarStatus(false);
        handleSnackbarShow();
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result;
        uploadTicket(base64String);
      };
      reader.readAsDataURL(file);
    } else {
      setSnackbarMessage("Please select a valid file.");
      setSnackbarStatus(false);
      handleSnackbarShow();
    }
  };

  const handleFilter = (filterData, type) => {
    setSelectedRows([]);
    setAllSelected(false);
    setItemOffset(0);
    setActiveFilters(filterData ?? "");
  };

  const uploadTicket = async (base64String) => {
    // Security: Validate input
    if (!base64String || typeof base64String !== "string") {
      setSnackbarMessage("Invalid file data");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }

    setLoading(true);
    try {
      const data = {
        upload_file: base64String,
      };
      const response = await apiCall("POST", "accounts_upload", data);

      if (response?.data?.success === true) {
        // Download report first before closing modal
        if (response?.data?.base64) {
          downloadBase64Excel(response.data.base64, "Account_Upload_Report", "Report file size exceeds 10MB limit");
        }
        
        setSnackbarMessage(response.data.message);
        setSnackbarStatus(true);
        handleSnackbarShow();
        setSelectedRows([]);
        setAllSelected(false);
        setShowImport(false);
        getAccountsList();
      } else {
        setSnackbarMessage(response?.data?.message || "Upload failed");
        setSnackbarStatus(false);
        handleSnackbarShow();

        if (response?.data?.base64) {
          downloadBase64Excel(response.data.base64, "Account_Upload_Error", "Error file size exceeds 10MB limit");
        }
      }
    } catch (err) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        fontFamily: "'Euclid Circular B', sans-serif",
        padding: "16px",
        height: "100vh",
        width: "100%",
        overflowX: "hidden",
        backgroundColor: "#f8f9fb",
      }}
    >
      <div
        style={{
          padding: "16px",
          borderRadius: "16px",
          backgroundColor: "#FFFFFF",
        }}
      >
        {isOpenDelete && <DeleteModal deleteUrl={isBulkDelete ? null : `account/delete/${deleteId}`} bulkDeleteApi={isBulkDelete ? bulkDeleteAccountsApi : null} bulkDeleteIds={isBulkDelete ? selectedRows : null} onSuccess={onSuccessDelete} onCancel={cancelDelete} />}
        <div style={{ marginBottom: "16px" }}>
          <div className="d-flex justify-content-between g-3">
            <div>
              {/* Accounts count */}
              <div className="fs-14 fw-600 text-dkblue">
                Accounts - {records}
              </div>
            </div>
            <div
              className="d-flex justify-content-end align-items-center"
              style={{ flexWrap: "wrap", gap: "12px" }}
            >
              {/* Search box */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    borderRadius: "100px",
                    border: "0.8px solid #e5e5e5",
                    padding: "9px 16px",
                    backgroundColor: "#ffffff",
                    height: "36px",
                  }}
                >
                  <img src={Search} alt="search" style={{ width: "16px" }} />
                  <input
                    type="search"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(sanitizeInput(e.target.value))}
                    style={{
                      border: "none",
                      outline: "none",
                      fontSize: "14px",
                      flex: 1,
                      backgroundColor: "transparent",
                    }}
                    className="no-focus-border"
                  />
                </div>
                <img
                  src={Filter}
                  alt="Filter"
                  style={{ height: "36px", width: "36px", cursor: "pointer" }}
                  onClick={() => setShowFilter(true)}
                />
              </div>

              {/* ✅ COMMON FILTER */}
              <CommonFilter
                type="accounts"
                open={showFilter}
                onClose={() => setShowFilter(false)}
                onSubmit={handleFilter}
              />

              {/* ✅ IMPORT MODAL */}
              <ImportFileModal
                type="accounts"
                open={showImport}
                onClose={() => setShowImport(false)}
                onExport={downloadTemplate}
                onImport={(base64) => uploadTicket(base64)}
              />

              {/* Create / Import / Schedules buttons */}
              <div className="d-flex justify-content-end align-items-center">
                {selectedRows.length === 0 && hasCreatePermission && (
                  <div style={{ position: "relative", display: "inline-block" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "8px 16px",
                        borderRadius: "100px",
                        borderBottom: "3px solid #1723C2",
                        background:
                          "linear-gradient(180deg, #6971DD 0%, #3D4AF5 100%)",
                        height: "40px",
                        width: "fit-content",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                        onClick={() => navigate("/accounts/create")}
                      >
                        <img src={Plus} style={{ width: "16px" }} alt="Plus" />
                        <div className="fs-15 fw-600 text-white">Create</div>
                      </div>
                    </div>
                  </div>
                )}
                {selectedRows.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      padding: "0 20px",
                      borderRadius: "50px",
                      borderBottom: "3px solid #B31717",
                      background:
                        "linear-gradient(180deg, #FF7A7A 0%, #D61916 100%)",
                      height: "40px",
                      width: "auto",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                    }}
                    onClick={handleBulkDelete}
                  >
                    <div className="fs-15 fw-600 text-white">Delete ({selectedRows.length})</div>
                  </div>
                )}
                <div
                  style={{
                    position: "relative",
                    display: "inline-block",
                    marginLeft: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "8px 16px",
                      borderRadius: "100px",
                      border: "1px solid #3D4AF5",
                      height: "36px",
                      width: "fit-content",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowImport(true)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <img src={ExcelImportIC} style={{ width: "16px" }} alt="Import" />
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "16px",
                          color: "#3D4AF5",
                        }}
                      >
                        Import
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    position: "relative",
                    display: "inline-block",
                    marginLeft: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "8px 16px",
                      borderRadius: "100px",
                      border: "1px solid #3D4AF5",
                      height: "36px",
                      width: "fit-content",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/accounts/outstanding-schedules")}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <img src={ScheduleIC} style={{ width: "16px" }} alt="Outstanding Schedules" />
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "16px",
                          color: "#3D4AF5",
                        }}
                      >
                        Schedules
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <Row>
            <Col sm={12}>
              <div
                style={{
                  borderRadius: "16px",
                  border: "1px solid #ebebeb",
                  backgroundColor: "#ffffff",
                  overflow: "hidden",
                }}
                className="table-scroll-container"
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    borderBottom: "1px solid #ebebeb",
                  }}
                >
                  <div
                    style={{
                      width: "72px",
                      padding: "10px 24px",
                      flexShrink: 0,
                      borderRight: "1px solid #ebebeb",
                    }}
                  >
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: columnsGridTemplate, flex: 1 }}>
                    {visibleOptions.map((label) => (
                        <div
                          key={label}
                          onClick={() => handleSortClick(label)}
                          title={`Sort by ${label}`}
                          style={{
                            minWidth: 0,
                            padding: "10px 16px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderRight: "1px solid #ebebeb",
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
                            <div
                              className="fs-12 fw-600 text-ltgrey"
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {label.toUpperCase()}
                            </div>
                            <span
                              className="fs-12 fw-600"
                              style={{ flexShrink: 0, color: sortColumn === label ? "#3D4AF5" : "#c4c9d4" }}
                            >
                              {sortColumn === label && sortDirection === "desc" ? "▼" : "▲"}
                            </span>
                          </div>
                          <img src={Dots} alt="dots" style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()} />
                        </div>
                    ))}
                  </div>

                  <div
                    onClick={() => setShowModal(true)}
                    style={{
                      width: "72px",
                      flexShrink: 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {/* Customize Icon (Header) */}
                    <img src={Customize} alt="customize" />
                  </div>
                </div>

                {/* Data Rows */}
                {listLoading ? (
                  <div style={{ width: "100%", padding: "40px 0" }}>
                    <Loader height="120px" />
                  </div>
                ) : sortedAccountsList.length === 0 ? (
                  <div style={{ width: "100%", textAlign: "center", padding: "40px 0", color: "#8790A1" }}>
                    No accounts found
                  </div>
                ) : (
                  sortedAccountsList.map((account, rowIndex) => (
                  <div
                    key={account.id}
                    onMouseEnter={() => setHoveredIndex(rowIndex)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      borderBottom: "1px solid #ebebeb",
                      backgroundColor: "#ffffff",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        width: "72px",
                        padding: "10px 24px",
                        flexShrink: 0,
                        borderRight: "1px solid #ebebeb",
                      }}
                    >
                      <input type="checkbox" checked={selectedRows.includes(account.id)} onChange={() => toggleRow(account.id)} disabled={!anyColumnSelected} />
                    </div>
                    <div
                      style={{ display: "grid", gridTemplateColumns: columnsGridTemplate, flex: 1, cursor: hasViewPermission ? "pointer" : "not-allowed" }}
                      onClick={() => {
                        if (hasViewPermission) {
                          navigate("/accounts/view", {
                            state: { id: account.id },
                          });
                        }
                      }}
                    >
                      {visibleOptions.map((label) => (
                        <div
                          key={label}
                          title={account[label]}
                          className="text-dkblue fs-14 fw-400"
                          style={{
                            minWidth: 0,
                            padding: "10px 16px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            borderRight: "1px solid #ebebeb",
                          }}
                        >
                          {label === "Status" ? (
                            <span style={{
                              padding: "4px 12px",
                              borderRadius: "100px",
                              fontSize: "12px",
                              fontWeight: "600",
                              backgroundColor: account[label] ? "#e6f4ea" : "#fce8e6",
                              color: account[label] ? "#1e7e34" : "#d93025"
                            }}>
                              {account[label] ? "Active" : "Inactive"}
                            </span>
                          ) : (
                            account[label] || "-"
                          )}
                        </div>
                      ))}
                    </div>
                    <div style={{ width: "72px", flexShrink: 0 }} />
                    {hoveredIndex === rowIndex && (
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          right: "12px",
                          transform: "translateY(-50%)",
                          zIndex: 1,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {hasEditPermission && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("/accounts/create", {
                                  state: { id: account.id },
                                });
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                border: "0.6px solid #E5E5E5",
                                cursor: "pointer",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "100px",
                                background: "#FFFFFF",
                              }}
                              title="Edit Account"
                            >
                              <EditRow />
                            </div>
                          )}
                          {hasEditPermission && hasDeletePermission && <span style={{ marginLeft: "16px" }}></span>}
                          {hasDeletePermission && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteId(account.id);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                border: "0.6px solid #E5E5E5",
                                cursor: "pointer",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "100px",
                                background: "#FFFFFF",
                              }}
                              title="Delete Account"
                            >
                              <DeleteRow />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  ))
                )}

                {showModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      backgroundColor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#fff",
                        padding: "24px",
                        borderRadius: "12px",
                        boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                        width: "300px",
                        maxHeight: "80vh",
                        overflowY: "auto",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "16px",
                          marginBottom: "16px",
                        }}
                      >
                        Select Columns
                      </div>
                      {options.map((label, index) => (
                        <label
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "10px",
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                        >
                          <input type="checkbox" checked={!!selectedColumns[label]} onChange={() => toggleColumn(label)} style={{ marginRight: "8px" }} />
                          {label}
                        </label>
                      ))}
                      <button
                        onClick={() => setShowModal(false)}
                        style={{
                          marginTop: "16px",
                          width: "100%",
                          padding: "10px",
                          borderRadius: "8px",
                          border: "none",
                          backgroundColor: "#3D4AF5",
                          color: "#fff",
                          fontWeight: "500",
                          cursor: "pointer",
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <div>
          <Row>
            <Col>
              <RecordsPerPage
                value={itemsPerPage}
                onChange={(event) => {
                  setItemsPerPage(event.target.value);
                }}
              />
            </Col>
            <Col>
              <div className="d-flex justify-content-end">
                <ReactPaginate
                  // Keyed on itemOffset so the (class-based) component fully
                  // remounts on every page change — its internal "selected"
                  // state is only initialized from forcePage in the
                  // constructor and doesn't reliably re-sync via
                  // componentDidUpdate for a value that was already correct
                  // on the very first render (e.g. itemOffset restored from
                  // sessionStorage on mount), which otherwise leaves the
                  // active page highlight stuck on page 1.
                  key={itemOffset}
                  previousLabel={<img src={lar} className="ar3" alt="previous" />}
                  nextLabel={<img src={rar} className="ar3" alt="next" />}
                  breakLabel={"..."}
                  pageCount={pageCount + 1}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={3}
                  onPageChange={handlePageClick}
                  forcePage={itemOffset}
                  containerClassName={"pagination"}
                  activeClassName={"active"}
                />
              </div>
            </Col>
          </Row>

          <ToastContainer position="top-end">
            <Toast className="d-inline-block m-1" bg={snackbarStatus === true ? "success" : "danger"} show={showSnackbar} onClose={handleSnackbarClose} delay={3000} autohide>
              <Toast.Body style={{ color: "#ffffff" }}>{snackbarMessage}</Toast.Body>
            </Toast>
          </ToastContainer>
        </div>
      <AiChat
        askApi={askAccountChatApi}
        providersApi={getAccountAiProvidersApi}
        title="Ask about accounts"
        hint="Try: “how many active accounts do we have?” or “break down accounts by status”"
      />
      </div>
    </div>
  );
};

export default AccountList;
